import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { emailService } from '../../services';
import { messageHandler } from '../../services/message-handler.service';
import type { EmailCredential } from '../../types/email';
import { cn } from '../../lib/utils';
import {
  Modal,
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextarea,
  Button,
} from '../ui';

interface EmailComposerProps {
  isOpen: boolean;
  onClose: () => void;
  replyTo?: {
    id: number;
    to: string;
    subject: string;
  };
  credentials?: EmailCredential[];
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function EmailComposer({ isOpen, onClose, replyTo, credentials = [] }: EmailComposerProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedCredential, setSelectedCredential] = useState<EmailCredential | null>(null);
  const queryClient = useQueryClient();

  const sendMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      if (replyTo) {
        return emailService.replyToEmail(replyTo.id, formData);
      }
      return emailService.sendEmail(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      queryClient.invalidateQueries({ queryKey: ['sent-emails'] });
      messageHandler.success('Email enviado com sucesso');
      onClose();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: acceptedFiles => {
      const validFiles = acceptedFiles.filter(file => file.size <= MAX_FILE_SIZE);
      const invalidFiles = acceptedFiles.filter(file => file.size > MAX_FILE_SIZE);

      if (invalidFiles.length > 0) {
        messageHandler.error(`Arquivos muito grandes: ${invalidFiles.map(f => f.name).join(', ')}`);
      }

      setSelectedFiles(prev => [...prev, ...validFiles]);
    },
    maxSize: MAX_FILE_SIZE,
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    // Add files to form data
    selectedFiles.forEach(file => {
      formData.append('attachments', file);
    });

    // Add signature if available
    const credentialId = formData.get('email_credential_id') as string;
    const credential = credentials.find(c => c.id === Number(credentialId));
    if (credential?.signature) {
      const body = formData.get('body') as string;
      formData.set('body', `${body}\n\n${credential.signature}`);
    }

    sendMutation.mutate(formData);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={replyTo ? 'Responder Email' : 'Novo Email'}
      size="xl"
    >
      <Form onSubmit={handleSubmit}>
        <FormField>
          <FormLabel htmlFor="email_credential_id" required>
            Conta de Email
          </FormLabel>
          <FormSelect
            id="email_credential_id"
            name="email_credential_id"
            required
            onChange={e => {
              const credential = credentials.find(c => c.id === Number(e.target.value));
              setSelectedCredential(credential || null);
            }}
          >
            <option value="">Selecione uma conta</option>
            {credentials?.map(credential => (
              <option key={credential.id} value={credential.id}>
                {credential.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="to_email" required>
            Para
          </FormLabel>
          <FormInput
            id="to_email"
            name="to_email"
            type="email"
            required
            defaultValue={replyTo?.to}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="subject" required>
            Assunto
          </FormLabel>
          <FormInput
            id="subject"
            name="subject"
            required
            defaultValue={replyTo ? `Re: ${replyTo.subject}` : ''}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="body" required>
            Mensagem
          </FormLabel>
          <FormTextarea id="body" name="body" required rows={10} />
          {selectedCredential?.signature && (
            <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-sm text-gray-500">
              <p className="font-medium mb-1">Assinatura:</p>
              <div dangerouslySetInnerHTML={{ __html: selectedCredential.signature }} />
            </div>
          )}
        </FormField>

        <FormField>
          <FormLabel>Anexos</FormLabel>
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors',
              isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-primary-500'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Arraste arquivos aqui ou clique para selecionar
            </p>
            <p className="text-xs text-gray-500 mt-1">Tamanho máximo: 10MB por arquivo</p>
          </div>

          {selectedFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                >
                  <div className="flex items-center">
                    <span className="text-sm font-medium">{file.name}</span>
                    <span className="ml-2 text-xs text-gray-500">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeFile(index)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </FormField>

        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
          <Button type="submit" isLoading={sendMutation.isPending} className="sm:col-start-2">
            Enviar
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
            Cancelar
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default EmailComposer;
